import React, { FC, useMemo, useState } from 'react';
import { Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select, TableContainer, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ZoomIn } from '@mui/icons-material';
import LogoSpinner from 'src/components/LogoSpinner';
import { DynamicTable, TableSearch } from 'src/components/DynamicTable/DynamicTable';
import { ReferralCodeUsageResponse, ReferralCodeUsageStatus } from '../Referral/referral.interface';
import { useReferralUsage } from 'src/hooks/useReferralUsage';

const TABLE_TEXT_STYLE = {
  p: '2%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

interface ReferralUsageContentProps {
  loading: boolean;
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  onRefresh?: () => void;
  usages: ReferralCodeUsageResponse[] | undefined;
}

export const ReferralUsageContent: FC<ReferralUsageContentProps> = ({
  loading,
  page,
  rowsPerPage,
  usages,
  onRefresh
}) => {
  const [filtered, setFiltered] = useState<ReferralCodeUsageResponse[]>(usages || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState<ReferralCodeUsageResponse | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState<ReferralCodeUsageStatus>('PENDING');

  const { loading: apiLoading, getReferralCodeUsageById, updateReferralCodeUsageStatus } = useReferralUsage();

  const statusChip = (status: ReferralCodeUsageStatus) => (
    <Chip title={status} label={status} color={status === 'COMPLETED' ? 'success' : status === 'PENDING' ? 'warning' : 'default'} size="small" />
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleView = async (row: ReferralCodeUsageResponse) => {
    setOpenDialog(true);
    const full = await getReferralCodeUsageById(row.id);
    setSelected(full || row);
  };

  const updateUsageStatus = async (newStatus: ReferralCodeUsageStatus) => {
    if (!selected) return;
    const ok = await updateReferralCodeUsageStatus(selected.id, { status: newStatus });
    if (ok) {
      onRefresh?.();
      const full = await getReferralCodeUsageById(selected.id);
      setSelected(full || { ...selected, status: newStatus });
      setIsEditingStatus(false);
    }
  };

  const headers = useMemo(
    () => [
      { headerName: 'Buyers Email', field: 'buyerEmail', minWidth: 160 },
      { headerName: 'Commission Amount', 
        field: 'commissionAmount', 
        minWidth: 160,
        renderCell: ({ row }) => (
          <Box sx={TABLE_TEXT_STYLE}>
            {row.commissionAmount
              ? `N${Number(row.commissionAmount).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`
              : 'N/A'}
          </Box>
        )
      },
      { headerName: 'Discount Amount Applied', field: 'discountAppliedAmount', minWidth: 160 },
      {
        headerName: 'Referal Code',
        field: 'referralCode',
        minWidth: 120,

      },
      {
        headerName: 'Status',
        field: 'status',
        minWidth: 140,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{statusChip(row.status)}</Box>
      },
      {
        headerName: 'Created',
        field: 'createdAt',
        minWidth: 140,
        renderCell: ({ row }) => <Box sx={TABLE_TEXT_STYLE}>{formatDate(row.createdAt)}</Box>
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filterable: false,
        width: 140,
        renderCell: ({ row }) => (
          <Button size="small" startIcon={<ZoomIn />} onClick={() => handleView(row)}>
            View
          </Button>
        )
      }
    ],
    []
  );

  if (loading || apiLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', py: '5%' }}>
        <LogoSpinner />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <TableContainer>
          <DynamicTable
            showPagination={true}
            rowsPerPage={rowsPerPage}
            page={page}
            totalPage={usages?.length ?? 0}
            searchElement={<TableSearch placeholder="Search Referral Usages" searchData={[]} setSearchData={setFiltered} />}
            rows={(usages || []).map((d) => ({
              ...d,
              rowHeight: 55
            }))}
            headers={headers}
          />
        </TableContainer>
      </Card>

      <Dialog fullWidth maxWidth="md" open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant="h4">Referral Code Usage Details</Typography>
            <IconButton color="error" onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selected ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Buyer Email</Typography>
                <Typography variant="body1">{selected.buyerEmail}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Discount Amount Applied</Typography>
                <Typography variant="body1">{selected.discountAppliedAmount}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Referral</Typography>
                <Typography variant="body1">{selected.referralCode}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {statusChip(selected.status)}
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2">Created</Typography>
                <Typography variant="body1">{formatDate(selected.createdAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Updated</Typography>
                <Typography variant="body1">{formatDate(selected.updatedAt)}</Typography>
              </Box>

              <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Update Status
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={statusValue}
                      onChange={(e) => setStatusValue(e.target.value as ReferralCodeUsageStatus)}
                    >
                      {['PENDING', 'COMPLETED', 'CANCELLED'].map((s) => (
                        <MenuItem value={s} key={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="outlined" onClick={() => setIsEditingStatus((v) => !v)}>
                    {isEditingStatus ? 'Cancel' : 'Edit Status'}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!isEditingStatus}
                    onClick={() => updateUsageStatus(statusValue)}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setOpenDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
